
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {CURSOR_MARKER_COLOR} from "../CursorPointMarker.js";
import {Line2} from "../../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../../libs/three.js/lines/LineMaterial.js";
import {Utils} from "../../utils.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {createFeature} from "../Feature.js";
import {createStyledLabel} from "../DigitizationStyle.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {computePerpendicularConstraint} from "../geometry/perpendicularConstraint.js";
import {promptDistance} from "../DistancePrompt.js";
import {settings} from "../settings/DigitizationSettings.js";

import {t} from "../i18n/index.js";
// Shared capture flow for the two related "Vinkelret" tools (Vinkelret
// punkt / Vinkelret linje) - same architectural principle as the existing
// Midpoint/Midline pair: one shared geometric core (see
// geometry/perpendicularConstraint.js), two thin tool registrations
// (perpendicularPointTool.js / perpendicularLineTool.js) that only differ in
// `resultMode` and their own metadata/instructions.
//
//   PerpendicularConstraint (geometry/perpendicularConstraint.js)
//           |
//           +-- runPerpendicularCapture(..., "point")  - Vinkelret punkt
//           |
//           +-- runPerpendicularCapture(..., "line")   - Vinkelret linje
//
// Phase A (baseline): reuses DigitizationModule._captureVector() unchanged -
// the same throwaway, never-committed, never-snap-registered construction
// polyline Midline's own two base vectors use. Since it is never registered
// via registerFeatureSnapTargets(), its own vertices structurally cannot
// attract the perpendicular tool's later point-cloud picks via snapping -
// there is nothing to special-case.
//
// Phase B (perpendicular capture): the constraint is applied unconditionally
// AFTER DigitizationModule._pickPlacementPosition() resolves a pick (whether
// that came from the point cloud or from snapping to some *other*,
// already-registered feature's vertex) - the constrained XY always wins,
// with Z kept from whatever was picked. That is the "clearly defined,
// consistent priority" the task calls for: perpendicular constraint always
// overrides XY, snapping never gets the final say on it.
//
// Live preview is driven by the real point-cloud pick (via
// _pickPlacementPosition on every mousemove), not a screen-space-only
// projection like Midline's connector - Z must come from the actual pick,
// which a screen-space-only preview (Midline's technique, chosen there
// specifically to avoid point-cloud gaps) can't provide.

// Exported so parallelOffsetTool.js (same "Perpendicular / Offset geometry"
// tool family) can reuse these instead of a fourth copy - see this module's
// own doc comment for the shared/separate split across the family.
export function createDashedLine(color){
	const geometry = new LineGeometry();
	geometry.setPositions([0, 0, 0, 0, 0, 0]);
	const material = new LineMaterial({color, linewidth: 2, dashSize: 3, gapSize: 2, dashed: true, resolution: new THREE.Vector2(1000, 1000)});
	material.depthTest = false;
	const line = new Line2(geometry, material);
	line.computeLineDistances();
	line.visible = false;
	return line;
}

export function createMarker(color){
	// world-space radius 1, matching FeatureVisual's convention, so
	// ScreenSpaceScale's targetPixels values apply directly
	const geometry = new THREE.SphereGeometry(1, 16, 16); // kugle (var oktaeder/"pyramide") - samme form som markøren og punkterne
	const material = new THREE.MeshLambertMaterial({color, depthTest: false, depthWrite: false});
	const mesh = new THREE.Mesh(geometry, material);
	mesh.visible = false;
	return mesh;
}

export function setLinePositions(line, a, b){
	line.position.copy(a);
	line.geometry.setPositions([0, 0, 0, ...b.clone().sub(a).toArray()]);
	line.geometry.verticesNeedUpdate = true;
	line.geometry.computeBoundingSphere();
	line.computeLineDistances();
}

const PREVIEW_COLOR = 0xffdc00; // matches the existing "computed/preview point" convention (Midpoint, Midline, Pipe)

// `resultMode`: "point" - a single left-click commits one Point feature and
// the tool stops; "line" - repeated left-clicks each commit one vertex of a
// growing LineString, right-click finishes it (>=2 vertices required).
//
// Shown FIRST, before any of the registration workflow starts (including
// baseline capture) - see the shared "Vinkelret / parallel" distance prompt
// requirement (DistancePrompt.js). Cancelling it aborts the tool activation
// entirely, same as pipeTool.js's own wall-thickness prompt cancellation.
export function runPerpendicularCapture(dm, tool, resultMode){
	const defaultValue = settings.get("perpendicularParallel.defaultDistance");

	promptDistance(dm, {
		title: tool.name,
		description: t("Angiv en fast afstand fra basislinjen, eller lad feltet stå tomt for at vælge afstanden dynamisk med musen under registrering."),
		defaultValue,
	}).then((fixedDistance) => {
		if(fixedDistance === undefined){
			// cancelled - never start the capture workflow
			if(dm.activeTool === tool) dm.stopInsertion();
			return;
		}

		dm._showInstruction(tool.name, tool.instructions.baseline);

		dm._captureVector((baseline) => {
			if(!baseline) return; // cancelled, or fewer than 2 points captured

			runPerpendicularPhase(dm, tool, baseline, resultMode, fixedDistance);
		});
	});
}

function runPerpendicularPhase(dm, tool, baseline, resultMode, fixedDistance){
	baseline.visible = settings.get("perpendicularParallel.showBaselineDuringCapture");

	const viewer = dm.viewer;
	const domElement = viewer.renderer.domElement;

	dm._showInstruction(tool.name, tool.instructions.perpendicular);

	const qMarker = createMarker(PREVIEW_COLOR);
	const perpLine = createDashedLine(PREVIEW_COLOR);
	const candidateMarker = createMarker(CURSOR_MARKER_COLOR);
	const candidateLabel = createStyledLabel("");
	dm.scene.add(qMarker, perpLine, candidateMarker, candidateLabel);

	// "line" mode only: the growing result polyline, and its own live
	// edge-preview from the last committed vertex to the live candidate
	// (mirrors midlineTool.js's previewEdge)
	const resultVisual = resultMode === "line" ? new FeatureVisual("LineString") : null;
	if(resultVisual){
		resultVisual.vertexRoleNames = [];
		dm.scene.add(resultVisual);
	}
	const previewEdge = resultMode === "line" ? createDashedLine(PREVIEW_COLOR) : null;
	if(previewEdge) dm.scene.add(previewEdge);

	let currentConstrained = null;
	let pendingPointPosition = null;
	let finished = false;

	const updateScales = () => {
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(new THREE.Vector2());
		const w = size.width, h = size.height;
		const cameraPos = camera.position;

		if(qMarker.visible){
			const s = computeScreenSpaceScale(camera, qMarker.getWorldPosition(new THREE.Vector3()), cameraPos, w, h, ScreenSpaceSizes.computedPoint);
			qMarker.scale.set(s, s, s);
		}
		if(candidateMarker.visible){
			const s = computeScreenSpaceScale(camera, candidateMarker.getWorldPosition(new THREE.Vector3()), cameraPos, w, h, ScreenSpaceSizes.computedPoint);
			candidateMarker.scale.set(s, s, s);
		}
		if(candidateLabel.visible){
			const s = computeScreenSpaceScale(camera, candidateLabel.getWorldPosition(new THREE.Vector3()), cameraPos, w, h, ScreenSpaceSizes.label);
			candidateLabel.scale.set(s, s, s);
		}
		perpLine.material.resolution.set(w, h);
		if(previewEdge) previewEdge.material.resolution.set(w, h);
	};

	const onUpdate = () => updateScales();
	viewer.addEventListener("update", onUpdate);

	const hidePreview = () => {
		qMarker.visible = false;
		perpLine.visible = false;
		candidateMarker.visible = false;
		candidateLabel.visible = false;
		if(previewEdge) previewEdge.visible = false;
		currentConstrained = null;
	};

	// Locks the picked point's distance from `q` to `fixedDistance` while
	// keeping whatever side/direction the cursor chose - capture-flow-only
	// logic (reads computePerpendicularConstraint()'s public q/constrained
	// result, doesn't touch that function or its geometry). No-op when there
	// is no fixed distance, or the picked point coincides with `q` (nothing
	// to preserve a direction from that frame).
	const applyFixedDistance = (result) => {
		if(fixedDistance === null) return result;

		const dx = result.constrained.x - result.q.x;
		const dy = result.constrained.y - result.q.y;
		const mag = Math.hypot(dx, dy);
		if(mag < 1e-9) return result;

		const scale = fixedDistance / mag;
		return {
			...result,
			constrained: new THREE.Vector3(
				result.q.x + dx * scale,
				result.q.y + dy * scale,
				result.constrained.z,
			),
		};
	};

	const updatePreview = (picked) => {
		const baselinePoints = baseline.points.map((p) => p.position);
		let result = computePerpendicularConstraint(picked, baselinePoints);
		if(!result){ hidePreview(); return; }

		result = applyFixedDistance(result);
		currentConstrained = result.constrained;

		qMarker.visible = settings.get("perpendicularParallel.showProjectionPoint");
		if(qMarker.visible) qMarker.position.copy(result.q);

		candidateMarker.position.copy(result.constrained);
		candidateMarker.visible = true;

		perpLine.visible = settings.get("perpendicularParallel.showHelperLines");
		if(perpLine.visible) setLinePositions(perpLine, result.q, result.constrained);

		const x = Utils.addCommas(result.constrained.x.toFixed(3));
		const y = Utils.addCommas(result.constrained.y.toFixed(3));
		const z = Utils.addCommas(result.constrained.z.toFixed(3));
		let labelText = `${tool.name} (preview)\nX: ${x}\nY: ${y}\nZ: ${z}`;
		if(settings.get("perpendicularParallel.showDistance")){
			const distance = Math.hypot(result.constrained.x - result.q.x, result.constrained.y - result.q.y);
			labelText += t("\nAfstand: {0} m", distance.toFixed(3));
		}
		candidateLabel.setText(labelText);
		candidateLabel.position.copy(result.constrained);
		candidateLabel.visible = true;

		if(resultVisual && resultVisual.points.length > 0){
			const last = resultVisual.points[resultVisual.points.length - 1].position;
			setLinePositions(previewEdge, last, result.constrained);
			previewEdge.visible = true;
		}else if(previewEdge){
			previewEdge.visible = false;
		}
	};

	const onMouseMove = (e) => {
		if(finished || dm.isPaused()) return;
		const picked = dm._pickPlacementPosition(e);
		if(!picked){ hidePreview(); return; }
		updatePreview(picked);
	};

	const removeConstructionGeometry = () => {
		dm.scene.remove(baseline);
		dm.scene.remove(qMarker);
		dm.scene.remove(perpLine);
		dm.scene.remove(candidateMarker);
		dm.scene.remove(candidateLabel);
		if(previewEdge) dm.scene.remove(previewEdge);
	};

	const cleanup = () => {
		domElement.removeEventListener('mousemove', onMouseMove, false);
		domElement.removeEventListener('mouseup', onMouseUp, false);
		viewer.removeEventListener('update', onUpdate);
		viewer.removeEventListener('cancel_insertions', onCancel);
	};

	const finalizePoint = (position) => {
		const visual = new FeatureVisual("Point");
		visual.appendAtEnd(position.clone());

		const feature = createFeature({
			geometryType: "Point",
			coordinates: visual.toCoordinates(),
			category: tool.category,
			tool: tool.id,
			featureType: tool.featureType || tool.name,
			name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		});

		visual.setInfo(feature.properties.name, feature.properties.description);
		dm.scene.add(visual);

		dm._wireFeatureLifecycle(feature, visual);
		dm.dispatchEvent({type: "feature_created", feature, visual});
	};

	const finalizeLine = () => {
		if(!resultVisual || resultVisual.points.length < 2){
			if(resultVisual) dm.scene.remove(resultVisual);
			return;
		}

		const coordinates = resultVisual.toCoordinates();
		const feature = createFeature({
			geometryType: "LineString",
			coordinates,
			category: tool.category,
			tool: tool.id,
			featureType: tool.featureType || tool.name,
			name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		});

		resultVisual.vertexRoleNames = resultVisual.points.map((p, i) => `${tool.name} ${i + 1}`);
		resultVisual.setInfo(feature.properties.name, feature.properties.description);

		dm._wireFeatureLifecycle(feature, resultVisual);
		dm.dispatchEvent({type: "feature_created", feature, visual: resultVisual});
	};

	// shared finish for both the "point" mode's committing left-click and the
	// "line" mode's completing right-click - see onMouseUp
	const finish = () => {
		if(finished) return;
		finished = true;
		cleanup();
		removeConstructionGeometry();

		if(resultMode === "point" && pendingPointPosition){
			finalizePoint(pendingPointPosition);
		}else if(resultMode === "line"){
			finalizeLine();
		}

		// single-shot, like Pipe: the user must reselect the tool to digitize
		// another baseline+result - matches the task's own state diagram,
		// which ends at a terminal COMPLETE state rather than looping back
		if(dm.activeTool === tool) dm.stopInsertion();
	};

	const onCancel = (e) => {
		if(finished) return;
		finished = true;
		cleanup();
		removeConstructionGeometry();

		if(e && e.discard){
			// ESC: hard cancel at *any* phase - never commit anything, even a
			// result line's already-locked vertices (explicit task requirement)
			if(resultVisual) dm.scene.remove(resultVisual);
		}else if(resultMode === "line"){
			// tool switched away / stopped externally (not ESC): keep
			// whatever result line was already validly captured, same
			// convention as every other tool's non-discard cancel path
			finalizeLine();
		}
		// "point" mode has nothing to preserve here: committing a point IS
		// finishing (see onMouseUp), so onCancel only ever fires pre-commit
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;

		if(e.button === THREE.MOUSE.LEFT){
			if(!currentConstrained) return;

			if(resultMode === "point"){
				pendingPointPosition = currentConstrained.clone();
				finish();
			}else{
				const index = resultVisual.points.length;
				resultVisual.appendAtEnd(currentConstrained.clone());
				resultVisual.vertexRoleNames[index] = `${tool.name} ${index + 1}`;
				resultVisual._refreshVertexLabel(index);

				// hide the dynamic preview immediately - same reasoning as
				// midlineTool.js: never let it coincide on-screen with the
				// vertex just committed, and guard against a stray extra
				// mouseup re-committing the same stale position
				hidePreview();
			}
		}else if(e.button === THREE.MOUSE.RIGHT){
			if(resultMode === "line" && resultVisual.points.length >= 2){
				finish();
			}
			// "point" mode, or too few points yet in "line" mode: right-click
			// has no defined action here - ESC remains the only cancel path
		}
	};

	domElement.addEventListener('mousemove', onMouseMove, false);
	domElement.addEventListener('mouseup', onMouseUp, false);
	viewer.addEventListener('cancel_insertions', onCancel);
}
